import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

interface InputDialogProps {
  isOpen: boolean;
  inputCount: number;
  onConfirm: (inputs: string[]) => void;
  onCancel: () => void;
}

/**
 * Input 输入对话框组件
 * 
 * 当检测到代码中包含 input() 调用时，弹出此对话框
 * 让用户一次性填写所有输入值，避免阻塞等待
 */
export default function InputDialog({
  isOpen,
  inputCount,
  onConfirm,
  onCancel,
}: InputDialogProps) {
  const [inputs, setInputs] = useState<string[]>([]);

  // 初始化输入框数组
  useEffect(() => {
    if (isOpen) {
      setInputs(Array(inputCount).fill(''));
    }
  }, [isOpen, inputCount]);

  const handleInputChange = (index: number, value: string) => {
    const newInputs = [...inputs];
    newInputs[index] = value;
    setInputs(newInputs);
  };

  const handleConfirm = () => {
    onConfirm(inputs);
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    // 按 Enter 跳转到下一个输入框
    if (e.key === 'Enter' && index < inputs.length - 1) {
      e.preventDefault();
      const nextInput = document.getElementById(`input-${index + 1}`);
      if (nextInput) {
        (nextInput as HTMLInputElement).focus();
      }
    }
    // 最后一个输入框按 Enter 确认
    if (e.key === 'Enter' && index === inputs.length - 1) {
      e.preventDefault();
      handleConfirm();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onCancel}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* 标题栏 */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-800">
              请输入测试数据
            </h3>
            <button
              onClick={onCancel}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* 内容区 */}
          <div className="px-6 py-4">
            <p className="text-sm text-gray-600 mb-4">
              检测到代码中包含 <code className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">{inputCount}</code> 个 input() 调用，请依次填写输入值：
            </p>

            <div className="space-y-3 max-h-64 overflow-y-auto">
              {inputs.map((value, index) => (
                <div key={index} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-700 min-w-[60px]">
                    输入 {index + 1}:
                  </span>
                  <input
                    id={`input-${index}`}
                    type="text"
                    value={value}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    placeholder={`第 ${index + 1} 个输入值`}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    autoFocus={index === 0}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* 底部按钮 */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              取消
            </button>
            <button
              onClick={handleConfirm}
              disabled={inputs.some(v => v.trim() === '')}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              开始运行
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
